// Subflow Node Component for Flow Builder

import React from 'react';
import { Handle, Position } from 'reactflow';
import {
  Box,
  Typography,
  Chip
} from '@mui/material';

interface SubflowNodeProps {
  data: {
    label: string;
    description?: string;
    subflowName?: string;
    inputVariables?: string[];
    outputVariables?: string[];
    status?: string;
    color?: string;
  };
  selected?: boolean;
}

const SubflowNode: React.FC<SubflowNodeProps> = ({ data, selected }) => {
  const { 
    label = 'Subflow', 
    description = 'Call another flow',
    subflowName = 'Select a subflow',
    inputVariables = [],
    outputVariables = [],
    status = 'idle',
    color = '#FFEAA7'
  } = data;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#4CAF50';
      case 'failed': return '#F44336';
      case 'running': return '#FF9800';
      default: return '#9E9E9E';
    }
  };

  return (
    <Box
      sx={{
        minWidth: 220,
        maxWidth: 300,
        background: 'linear-gradient(135deg, rgba(255, 234, 167, 0.1) 0%, rgba(255, 234, 167, 0.2) 100%)',
        border: selected ? `3px solid ${color}` : `2px solid ${color}`,
        borderRadius: '16px',
        padding: 2,
        boxShadow: selected 
          ? `0 8px 25px rgba(255, 234, 167, 0.4)` 
          : '0 4px 12px rgba(0,0,0,0.1)',
        transition: 'all 0.2s ease',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          boxShadow: '0 8px 25px rgba(255, 234, 167, 0.3)',
          transform: 'translateY(-2px)'
        }
      }}
    >
      {/* Top decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${color} 0%, rgba(255, 234, 167, 0.6) 100%)`
        }}
      />

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '12px',
            backgroundColor: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.4rem'
          }}
        >
          🔗
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>
            {label}
          </Typography>
          <Typography variant="caption" sx={{ color: '#666', lineHeight: 1 }}>
            Subflow Call
          </Typography>
        </Box>
        <Chip
          size="small"
          label={status}
          sx={{
            backgroundColor: getStatusColor(status),
            color: 'white',
            fontSize: '0.7rem',
            height: 20
          }}
        />
      </Box>

      {/* Description */}
      <Typography 
        variant="body2" 
        sx={{ 
          color: '#555', 
          mb: 1.5, 
          fontSize: '0.8rem',
          lineHeight: 1.3
        }}
      >
        {description}
      </Typography>

      {/* Subflow Details */}
      <Box
        sx={{
          background: 'rgba(255,255,255,0.8)',
          border: '1px solid rgba(255, 234, 167, 0.3)',
          borderRadius: '8px',
          p: 1.5,
          mb: 1
        }}
      >
        <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold', mb: 1, display: 'block' }}>
          Target Flow:
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#333', 
            fontWeight: 'medium',
            mb: 1,
            background: 'rgba(255, 234, 167, 0.2)',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '0.8rem'
          }}
        >
          {subflowName}
        </Typography>

        {/* Input/Output Variables */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold' }}>
              Inputs ({inputVariables.length}):
            </Typography>
            {inputVariables.length > 0 ? (
              <Box sx={{ mt: 0.5 }}>
                {inputVariables.slice(0, 2).map((variable, index) => (
                  <Typography key={index} variant="caption" sx={{ color: '#333', display: 'block' }}>
                    • {variable}
                  </Typography>
                ))}
                {inputVariables.length > 2 && (
                  <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic' }}>
                    +{inputVariables.length - 2} more...
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic' }}>
                None
              </Typography>
            )}
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold' }}>
              Outputs ({outputVariables.length}):
            </Typography>
            {outputVariables.length > 0 ? (
              <Box sx={{ mt: 0.5 }}>
                {outputVariables.slice(0, 2).map((variable, index) => (
                  <Typography key={index} variant="caption" sx={{ color: '#333', display: 'block' }}>
                    • {variable}
                  </Typography>
                ))}
                {outputVariables.length > 2 && (
                  <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic' }}>
                    +{outputVariables.length - 2} more...
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic' }}>
                None
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Connection Handles */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: color,
          border: '2px solid white',
          width: 12,
          height: 12
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: color,
          border: '2px solid white',
          width: 12,
          height: 12
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="success"
        style={{
          background: '#4CAF50',
          border: '2px solid white',
          width: 10,
          height: 10,
          top: '60%'
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="fault"
        style={{
          background: '#F44336',
          border: '2px solid white',
          width: 10,
          height: 10,
          top: '80%'
        }}
      />
    </Box>
  );
};

export default SubflowNode;